/**
 * EventBlock Component
 * Unified component for rendering shift and task time blocks on the calendar.
 * Styled with Tailwind CSS with a 4px left border accent.
 */

import { useRef, useState, useCallback } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { Globe } from 'lucide-react';
import type { ShiftListItemDto, Task, CalendarEventDto as CalendarEventType } from '@signalvote/shared';
import { ShiftStatus, TaskStatus, assertNever } from '@signalvote/shared';
import {
  HOUR_HEIGHT,
  MINIMUM_BLOCK_HEIGHT,
  SNAP_MINUTES,
  calculateShiftTop,
  calculateShiftHeightFromMinutes,
  calculateTaskTop,
  calculateEndTime,
  calculateEndTimeFromMinutes,
  formatTime12Hour,
  getTimePartsInTimezone,
  toTimeStringInTimezone,
  type EventPosition,
} from '@/lib/calendarUtils';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useMayManageTasks } from '@/hooks';
import {
  getAssignmentDirection,
  getAssigneeInitials,
  formatDurationShort,
  getTaskPalette,
  getCategoryChipClasses,
  getCategoryAbbreviation,
  type TaskDirectionPalette,
} from '@/lib/taskAssignmentUtils';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { getShiftStatusMeta } from './shiftStatusVocab';
import { BLOCK_HOVER_CLASSES, SHIFT_HOVER_CLASSES, cascadeStackClass } from './shiftBlockClasses';

// ============================================================================
// Types
// ============================================================================

export interface ShiftBlockProps {
  type: 'shift';
  shift: ShiftListItemDto;
  status: ShiftStatus;
  claimedBy?: string;
  onClick: (shift: ShiftListItemDto) => void;
  completionPercentage?: number;
  position?: EventPosition;
  /** True when this is the cascaded block the user last clicked, so it sits in front. */
  isRaised?: boolean;
}

export interface TaskBlockProps {
  type: 'task';
  task: Task;
  onCheckboxClick: (taskId: string) => void;
  onResize?: (taskId: string, newDuration: number) => void;
  onClick: (task: Task) => void;
  position?: EventPosition;
  /** True when this is the cascaded block the user last clicked, so it sits in front. */
  isRaised?: boolean;
}

export interface CalendarEventBlockProps {
  type: 'calendar-event';
  event: CalendarEventType;
  onClick?: (event: CalendarEventType) => void;
  position?: EventPosition;
  timeZoneId?: string;
}

export type EventBlockProps = ShiftBlockProps | TaskBlockProps | CalendarEventBlockProps;

// ============================================================================
// Status Configuration
// ============================================================================

/**
 * Get Tailwind classes for status badge based on background context.
 * Extracted to avoid nested ternaries in JSX.
 */
function getStatusBadgeClasses(hasDarkBackground: boolean, isCompleted: boolean): string {
  if (hasDarkBackground) {
    return 'bg-white/25 text-white';
  }
  if (isCompleted) {
    return 'bg-emerald-500 text-white';
  }
  return 'bg-emerald-200 text-emerald-800';
}

/**
 * Get Tailwind classes for territory name text based on background context.
 */
function getTerritoryTextClasses(hasDarkBackground: boolean, isCompleted: boolean): string {
  if (hasDarkBackground) {
    return 'text-white';
  }
  if (isCompleted) {
    return 'text-emerald-800';
  }
  return 'text-emerald-900';
}

function getResizeHandleColor(isResizing: boolean, palette: TaskDirectionPalette): string {
  const opacity = isResizing ? 'opacity-100' : 'opacity-0 group-hover:opacity-100';
  const bg = isResizing ? palette.handleActive : palette.handle;
  return `${bg} ${opacity}`;
}

interface AssignmentChipProps {
  direction: 'inbound' | 'outbound';
  initials: string;
  size?: 'sm' | 'xs';
}

const DIRECTION_CHIP_STYLES = {
  inbound: { arrow: '\u2190', border: 'border border-blue-300 text-blue-600', bg: 'bg-blue-50' },
  outbound: { arrow: '\u2192', border: 'border border-violet-300 text-violet-600', bg: 'bg-violet-50' },
} as const;

function AssignmentChip({ direction, initials, size = 'sm' }: AssignmentChipProps) {
  const { arrow, border, bg } = DIRECTION_CHIP_STYLES[direction];
  const isSmall = size === 'sm';

  return (
    <span className={cn(
      'rounded font-medium inline-block',
      isSmall ? 'text-[0.55rem] px-1 py-0.5' : 'text-[0.5rem] px-1 py-0',
      border,
      isSmall && bg
    )}>
      {arrow}{isSmall ? ' ' : ''}{initials}
    </span>
  );
}

// ============================================================================
// Shift Block Sub-Component
// ============================================================================

function ShiftBlock({
  shift,
  status,
  claimedBy,
  onClick,
  completionPercentage,
  position,
  isRaised = false,
}: Omit<ShiftBlockProps, 'type'>) {
  const top = calculateShiftTop(shift.startTime);
  const height = calculateShiftHeightFromMinutes(shift.estimatedMinutes);
  const endTime = calculateEndTimeFromMinutes(shift.startTime, shift.estimatedMinutes);
  const statusMeta = getShiftStatusMeta(status);
  const StatusIcon = statusMeta.icon;

  const isCompleted = status === ShiftStatus.Completed;
  // Dark background shifts need white text (Claimed, InProgress)
  const hasDarkBackground =
    status === ShiftStatus.Claimed ||
    status === ShiftStatus.InProgress;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick(shift);
  };

  // Status-based styling (emerald color scheme to match month view)
  const getContainerClasses = () => {
    switch (status) {
      case ShiftStatus.Available:
        return 'bg-white border-dashed border-[1.5px] border-emerald-500 border-l-4 border-l-emerald-500 shadow-sm';
      case ShiftStatus.Claimed:
        return 'bg-emerald-600 border-l-4 border-l-emerald-400 shadow-md';
      case ShiftStatus.InProgress:
        return 'bg-emerald-700 border-l-4 border-l-emerald-400 shadow-md';
      case ShiftStatus.Completed:
        return 'bg-emerald-300 border-l-4 border-l-emerald-500 opacity-90 shadow-md';
      default:
        return assertNever(status);
    }
  };

  // Calculate left/width based on position (for side-by-side overlapping events)
  const positionStyle = position
    ? { left: position.left, width: position.width }
    : { left: '2px', width: 'calc(100% - 4px)' };

  const stackingClass = cascadeStackClass(position?.zIndex, isRaised);

  // Sharing the column costs horizontal room, so a real shift name (~28 chars)
  // no longer fits on two lines. Trade the duration row for a third name line:
  // the time range directly above already says how long the shift runs.
  const isSharingColumn = (position?.totalColumns ?? 1) > 1;
  const hasRoomForExtraRow = height >= 100;
  const showDuration = hasRoomForExtraRow && !isSharingColumn;

  const durationDisplay = formatDurationShort(shift.estimatedMinutes);

  return (
    <div
      data-event-block
      onClick={handleClick}
      className={cn(
        'absolute rounded-md p-1.5 cursor-pointer',
        'flex flex-col justify-between overflow-hidden',
        SHIFT_HOVER_CLASSES,
        getContainerClasses(),
        stackingClass
      )}
      style={{ top, height, ...positionStyle }}
    >
      {/* Status Badge */}
      <div className="flex items-start justify-between">
        <div
          className={cn(
            'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[0.65rem] font-semibold',
            getStatusBadgeClasses(hasDarkBackground, isCompleted)
          )}
        >
          <StatusIcon className="h-3.5 w-3.5" />
          <span>
            {status === ShiftStatus.Completed && completionPercentage !== undefined
              ? `${statusMeta.label} ${completionPercentage}%`
              : statusMeta.label}
          </span>
        </div>
      </div>

      {/* Shift Name */}
      <p
        className={cn(
          'text-xs font-semibold leading-tight mt-1',
          isSharingColumn && hasRoomForExtraRow ? 'line-clamp-3' : 'line-clamp-2',
          getTerritoryTextClasses(hasDarkBackground, isCompleted)
        )}
      >
        {shift.name}
      </p>

      {/* Walkbook Name (if available) */}
      {shift.walkbookName && height >= 90 && (
        <p
          className={cn(
            'text-[0.65rem] leading-tight line-clamp-1',
            hasDarkBackground ? 'text-white/80' : 'text-emerald-700'
          )}
        >
          {shift.walkbookName}
        </p>
      )}

      {/* Time Range */}
      <p
        className={cn(
          'text-[0.7rem]',
          hasDarkBackground ? 'text-white/85' : 'text-emerald-700'
        )}
      >
        {formatTime12Hour(shift.startTime)} - {formatTime12Hour(endTime)}
      </p>

      {/* Duration display */}
      {showDuration && (
        <div className="flex items-center gap-0.5">
          <span
            className={cn(
              'text-[0.65rem]',
              hasDarkBackground ? 'text-white/80' : 'text-emerald-600'
            )}
          >
            {durationDisplay}
          </span>
        </div>
      )}

      {/* Claimer info (only for claimed/in-progress shifts with enough height) */}
      {claimedBy && height >= 120 && (
        <p className={cn(
          'text-[0.6rem] italic mt-1',
          hasDarkBackground ? 'text-white/70' : 'text-emerald-600'
        )}>
          {claimedBy}
        </p>
      )}
    </div>
  );
}

// ============================================================================
// Task Block Sub-Component
// ============================================================================

function TaskBlock({
  task,
  onCheckboxClick,
  onResize,
  onClick,
  position,
  isRaised = false,
}: Omit<TaskBlockProps, 'type'>) {
  // Temp tasks (optimistic, not yet confirmed by server) are non-interactive
  const isTemp = task.id.startsWith('temp-');
  const mayManageTasks = useMayManageTasks();

  // Draggable setup - DragOverlay handles visual feedback, so we don't apply
  // the transform here. When dragging, we hide this element via opacity: 0.
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: task.id,
    data: { task },
    disabled: isTemp || !mayManageTasks,
  });

  // Resize state - tracks temporary duration while user drags the resize handle
  const [resizingDuration, setResizingDuration] = useState<number | null>(null);
  const resizeRef = useRef<{ startY: number; originalDuration: number } | null>(null);
  // Blocks the click event that fires after pointerup on resize
  const justResizedRef = useRef(false);

  const handleResizePointerDown = useCallback((e: React.PointerEvent) => {
    if (isTemp) return;
    e.stopPropagation(); // Prevent dnd-kit drag activation
    e.preventDefault();
    const original = task.durationMinutes || 60;
    resizeRef.current = { startY: e.clientY, originalDuration: original };
    setResizingDuration(original);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [task.durationMinutes, isTemp]);

  /** Calculate snapped duration from pointer Y delta relative to resize start */
  const getSnappedDuration = useCallback((clientY: number): number | null => {
    if (!resizeRef.current) return null;
    const deltaY = clientY - resizeRef.current.startY;
    const deltaMinutes = (deltaY / HOUR_HEIGHT) * 60;
    const snappedDelta = Math.round(deltaMinutes / SNAP_MINUTES) * SNAP_MINUTES;
    return Math.max(SNAP_MINUTES, resizeRef.current.originalDuration + snappedDelta);
  }, []);

  const handleResizePointerMove = useCallback((e: React.PointerEvent) => {
    const duration = getSnappedDuration(e.clientY);
    if (duration !== null) setResizingDuration(duration);
  }, [getSnappedDuration]);

  const handleResizePointerUp = useCallback((e: React.PointerEvent) => {
    const newDuration = getSnappedDuration(e.clientY);
    if (newDuration === null) return;
    const original = resizeRef.current!.originalDuration;
    resizeRef.current = null;
    setResizingDuration(null);
    // Block the click event that fires after pointerup
    justResizedRef.current = true;
    requestAnimationFrame(() => { justResizedRef.current = false; });
    if (newDuration !== original && onResize) {
      onResize(task.id, newDuration);
    }
  }, [getSnappedDuration, task.id, onResize]);

  // Hooks must be called before any early return
  const { user } = useAuth();

  // Only render if task has both dueDate and dueTime (scheduled tasks)
  if (!task.dueDate || !task.dueTime) {
    return null;
  }

  const activeDuration = resizingDuration ?? (task.durationMinutes || 60);
  const top = calculateTaskTop(task.dueTime);
  const height = (activeDuration / 60) * HOUR_HEIGHT;
  const isCompleted = task.status === TaskStatus.Completed;
  const isResizing = resizingDuration !== null;
  const isCompact = height < 40; // 30min or less - single-row layout

  // Assignment direction and color palette
  const direction = getAssignmentDirection(task, user?.id);
  const palette = getTaskPalette(isCompleted, direction);
  const assigneeInitials = getAssigneeInitials(task, direction);

  // Calculate left/width based on position (for side-by-side overlapping events)
  const positionStyle = position
    ? { left: position.left, width: position.width }
    : { left: '2px', width: 'calc(100% - 4px)' };

  const stackingClass = cascadeStackClass(position?.zIndex, isRaised);

  // Calculate end time for display (convert minutes to hours for calculateEndTime)
  const durationHours = activeDuration / 60;
  const endTime = calculateEndTime(task.dueTime, durationHours);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isTemp || justResizedRef.current) return;
    onClick(task);
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isTemp) return;
    onCheckboxClick(task.id);
  };

  return (
    <TooltipProvider delayDuration={400}>
      <Tooltip open={isDragging || isResizing ? false : undefined}>
        <TooltipTrigger asChild>
          <div
            ref={setNodeRef}
            style={{ top, height, ...positionStyle }}
            data-event-block
            {...listeners}
            {...attributes}
            onClick={handleClick}
            className={cn(
              'absolute rounded-md overflow-hidden shadow-sm',
              BLOCK_HOVER_CLASSES,
              isCompact ? 'px-1 py-0' : 'p-1.5',
              isDragging ? 'cursor-grabbing opacity-0' : 'cursor-pointer',
              !isDragging && palette.block,
              isResizing && palette.ring,
              stackingClass
            )}
          >
            {isCompact ? (
              /* Compact layout for ≤30min: single row with checkbox, title, time */
              <div className="flex items-center gap-1 h-full min-w-0">
                <div
                  onPointerDown={(e) => e.stopPropagation()}
                  className="inline-flex shrink-0"
                >
                  <input
                    type="checkbox"
                    checked={isCompleted}
                    onChange={() => {}}
                    onClick={handleCheckboxClick}
                    className={cn(
                      'h-3 w-3 rounded cursor-pointer',
                      palette.checkbox
                    )}
                  />
                </div>
                <span
                  className={cn(
                    'text-[0.65rem] font-semibold leading-none truncate',
                    palette.title
                  )}
                >
                  {task.title}
                </span>
                {activeDuration > SNAP_MINUTES && (
                  <span
                    className={cn(
                      'text-[0.55rem] leading-none shrink-0 ml-auto',
                      palette.secondary
                    )}
                  >
                    {formatTime12Hour(task.dueTime)}&ndash;{formatTime12Hour(endTime)}
                  </span>
                )}
                {/* Category chip - compact */}
                {task.categoryName && (
                  <span className={cn(getCategoryChipClasses(task.categoryName), 'shrink-0 ml-1')}>
                    {getCategoryAbbreviation(task.categoryName)}
                  </span>
                )}
                {/* Assignment chip - compact */}
                {assigneeInitials && direction && (
                  <span className="shrink-0 ml-1">
                    <AssignmentChip direction={direction} initials={assigneeInitials} size="xs" />
                  </span>
                )}
              </div>
            ) : (
              /* Normal layout for >30min: stacked checkbox+title, time range */
              <div className="flex flex-col h-full">
                <div className="flex items-start gap-1">
                  <div
                    onPointerDown={(e) => e.stopPropagation()}
                    className="inline-flex shrink-0"
                  >
                    <input
                      type="checkbox"
                      checked={isCompleted}
                      onChange={() => {}}
                      onClick={handleCheckboxClick}
                      className={cn(
                        'h-4 w-4 rounded border-2 cursor-pointer mt-0.5',
                        palette.checkbox
                      )}
                    />
                  </div>
                  <p
                    className={cn(
                      'text-xs font-semibold leading-tight line-clamp-2',
                      palette.title
                    )}
                  >
                    {task.title}
                  </p>
                </div>

                {height >= 55 && (
                  <p
                    className={cn(
                      'text-[0.7rem] mt-1',
                      palette.secondary
                    )}
                  >
                    {formatTime12Hour(task.dueTime)} - {formatTime12Hour(endTime)}
                  </p>
                )}
                {/* Category + Assignment chips */}
                {height >= 55 && (
                  <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                    {task.categoryName && (
                      <span className={getCategoryChipClasses(task.categoryName)}>
                        {getCategoryAbbreviation(task.categoryName)}
                      </span>
                    )}
                    {assigneeInitials && direction && (
                      <AssignmentChip direction={direction} initials={assigneeInitials} size="sm" />
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Resize handle - drag bottom edge to adjust duration */}
            {onResize && !isDragging && (
              <div
                onPointerDown={handleResizePointerDown}
                onPointerMove={handleResizePointerMove}
                onPointerUp={handleResizePointerUp}
                className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize group z-10"
              >
                <div className={cn(
                  'mx-auto w-8 h-1 rounded-full mt-0.5 transition-opacity',
                  getResizeHandleColor(isResizing, palette)
                )} />
              </div>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="bg-white text-gray-900 border border-gray-200 shadow-lg px-3 py-2 max-w-[220px]"
        >
          <div className="space-y-1">
            <p className="text-xs font-semibold leading-tight">{task.title}</p>
            <p className="text-[0.7rem] text-gray-600">
              {formatTime12Hour(task.dueTime)} - {formatTime12Hour(endTime)} · {formatDurationShort(activeDuration)}
            </p>
            <div className="flex items-center gap-1.5 flex-wrap">
              {task.categoryName && (
                <span className={getCategoryChipClasses(task.categoryName)}>
                  {getCategoryAbbreviation(task.categoryName)}
                </span>
              )}
              {assigneeInitials && direction && (
                <AssignmentChip direction={direction} initials={assigneeInitials} size="xs" />
              )}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ============================================================================
// Calendar Event Block Sub-Component (Google/Microsoft events)
// ============================================================================

function CalendarEventBlock({
  event,
  onClick,
  position,
  timeZoneId,
}: Omit<CalendarEventBlockProps, 'type'>) {
  const startParts = getTimePartsInTimezone(event.startTime, timeZoneId);
  const endParts = getTimePartsInTimezone(event.endTime, timeZoneId);

  const startMinutes = startParts.hours * 60 + startParts.minutes;
  const endMinutes = endParts.hours * 60 + endParts.minutes;
  const durationMinutes = Math.max(
    endMinutes > startMinutes ? endMinutes - startMinutes : 1440 - startMinutes,
    15
  );

  const top = (startMinutes / 60) * HOUR_HEIGHT;
  // Use actual time-proportional height — no minimum clamp so events stay in their slot
  const height = (durationMinutes / 60) * HOUR_HEIGHT;

  const positionStyle = position
    ? { left: position.left, width: position.width }
    : { left: '2px', width: 'calc(100% - 4px)' };

  const bgColor = event.calendarColor || '#4f46e5';

  const isOtherCampaign = event.isOtherCampaign ?? false;
  const isShowBusy = isOtherCampaign && event.visibility === 'ShowBusy';
  const isShowFull = isOtherCampaign && event.visibility === 'Show';

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isOtherCampaign) return; // Non-interactive for other-campaign events
    onClick?.(event);
  };

  // Layout thresholds based on actual pixel height
  const isCompact = height < 40;  // ≤30min: single-row
  const isMedium = height >= 40 && height < 70; // ~45min: badge + title only
  // >= 70px: full layout with time range

  const startTimeStr = formatTime12Hour(toTimeStringInTimezone(event.startTime, timeZoneId));
  const endTimeStr = formatTime12Hour(toTimeStringInTimezone(event.endTime, timeZoneId));

  const displayTitle = isShowBusy ? 'Busy' : event.title;
  const badgeName = isOtherCampaign && event.sourceCampaignName
    ? event.sourceCampaignName
    : event.calendarName;

  return (
    <div
      data-event-block
      onClick={handleClick}
      className={cn(
        'absolute rounded-md',
        isCompact ? 'px-1.5 py-0' : 'p-1.5',
        isOtherCampaign ? 'cursor-default pointer-events-none' : (onClick ? 'cursor-pointer hover:shadow-md' : 'cursor-default'),
        'overflow-hidden shadow-sm',
        'border-l-4',
        isShowBusy ? 'opacity-25' : isShowFull ? 'opacity-35' : 'opacity-60',
        'transition-shadow'
      )}
      style={{
        top,
        height,
        ...positionStyle,
        backgroundColor: `${bgColor}20`,
        borderLeftColor: bgColor,
      }}
    >
      {isCompact ? (
        /* Compact: single row — icon, title, time */
        <div className="flex items-center gap-1 h-full min-w-0">
          <Globe className="h-3 w-3 shrink-0" style={{ color: bgColor }} />
          <span className="text-[0.65rem] font-semibold leading-none truncate text-gray-800">
            {displayTitle}
          </span>
          <span className="text-[0.55rem] leading-none shrink-0 ml-auto text-gray-500">
            {startTimeStr}
          </span>
        </div>
      ) : isMedium ? (
        /* Medium: badge row + title, no time range */
        <div className="flex flex-col h-full">
          <div className="flex items-center gap-1">
            <Globe className="h-3 w-3 shrink-0" style={{ color: bgColor }} />
            <span className="text-[0.6rem] font-medium truncate" style={{ color: bgColor }}>
              {badgeName}
            </span>
          </div>
          <p className="text-xs font-semibold leading-tight mt-0.5 line-clamp-1 text-gray-800">
            {displayTitle}
          </p>
        </div>
      ) : (
        /* Full: badge, title, time range, optional location */
        <div className="flex flex-col h-full">
          <div className="flex items-center gap-1">
            <Globe className="h-3 w-3 shrink-0" style={{ color: bgColor }} />
            <span className="text-[0.6rem] font-medium truncate" style={{ color: bgColor }}>
              {badgeName}
            </span>
          </div>
          <p className="text-xs font-semibold leading-tight mt-0.5 line-clamp-2 text-gray-800">
            {displayTitle}
          </p>
          <p className="text-[0.7rem] mt-auto text-gray-600">
            {startTimeStr} - {endTimeStr}
          </p>
          {!isShowBusy && event.location && height >= 100 && (
            <p className="text-[0.6rem] text-gray-500 truncate">
              {event.location}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function EventBlock(props: EventBlockProps) {
  switch (props.type) {
    case 'shift': {
      const { type: _, ...rest } = props;
      return <ShiftBlock {...rest} />;
    }
    case 'task': {
      const { type: _, ...rest } = props;
      return <TaskBlock {...rest} />;
    }
    case 'calendar-event': {
      const { type: _, ...rest } = props;
      return <CalendarEventBlock {...rest} />;
    }
  }
}

export default EventBlock;
